import React, { useState, useRef, useEffect, ReactNode } from "react";
import styles from "./Accordion.module.scss";
import cn from "classnames";

import { Icon } from "../icon";

type AccordionProps = {
  title: string | ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  containerClassName?: string;
  isOpenExternal?: boolean;
  onToggle?: (isOpen: boolean) => void;
};

export const Accordion: React.FC<AccordionProps> = ({
  title,
  children,
  defaultOpen = false,
  containerClassName,
  isOpenExternal,
  onToggle,
}) => {
  const isControlled = typeof isOpenExternal === "boolean";

  const [isOpenInternal, setIsOpenInternal] = useState(defaultOpen);
  const isOpen = isControlled ? isOpenExternal! : isOpenInternal;

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [maxHeight, setMaxHeight] = useState<string>("0px");

  const idRef = useRef<string>(
    `accordion-${Math.random().toString(36).slice(2, 9)}`
  );

  useEffect(() => {
    if (!isControlled) {
      setIsOpenInternal(defaultOpen);
    }
  }, [defaultOpen, isControlled]);

  useEffect(() => {
    const contentEl = contentRef.current;
    if (!contentEl) {
      setMaxHeight("0px");
      return;
    }

    let ro: ResizeObserver | null = null;

    const updateHeight = () => {
      if (!contentEl) return;
      const sh = contentEl.scrollHeight;
      setMaxHeight(`${sh}px`);
    };

    if (isOpen) {
      updateHeight();
      ro = new ResizeObserver(updateHeight);
      ro.observe(contentEl);
    } else {
      setMaxHeight("0px");
    }

    return () => {
      if (ro) ro.disconnect();
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (isControlled) {
      onToggle?.(!isOpenExternal);
    } else {
      setIsOpenInternal((prev) => {
        const newState = !prev;
        onToggle?.(newState);
        return newState;
      });
    }
  };

  return (
    <div className={cn(styles.accordion, containerClassName)}>
      <button
        className={styles.header}
        type="button"
        aria-expanded={isOpen}
        aria-controls={idRef.current}
        onClick={handleToggle}
      >
        <span className={styles.title}>{title}</span>
        <Icon
          name="down-arrow"
          className={cn(styles.icon, { [styles.rotate]: isOpen })}
        />
      </button>

      <div
        id={idRef.current}
        ref={wrapperRef}
        className={styles.contentWrapper}
        style={{ maxHeight }}
      >
        <div ref={contentRef} className={styles.content}>
          {children}
        </div>
      </div>
    </div>
  );
};
